/**
 * Custom JSFX preset storage — user-saved scripts with their slider values,
 * persisted in localStorage. Matches the customDrumKits + customBassVoices
 * shape for consistency.
 *
 * Schema:
 *   { id:           "custom-fx-<random>",
 *     label:        "My Telephone EQ",
 *     source:       "@sample\nspl0 = spl0 * 0.5;",   // raw JSFX text
 *     sliderValues: { 1: 6, 2: 0.5 },                 // user's preferred params
 *     createdAt:    ISO date string }
 *
 * Storage key: `elpm-custom-jsfx-presets`.
 */

const STORAGE_KEY = "elpm-custom-jsfx-presets";

export function loadCustomFx() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveAll(list) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

export function addCustomFx({ label, source, sliderValues }) {
  const safeLabel = (label || "").trim().slice(0, 40) || "Untitled FX";
  // Limit source to 8 KB — anything bigger usually means the user has
  // a comment-heavy file or has accidentally pasted unrelated content.
  const safeSource = String(source || "").slice(0, 8 * 1024);
  const entry = {
    id: `custom-fx-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    label: safeLabel,
    source: safeSource,
    sliderValues: sliderValues && typeof sliderValues === "object" ? { ...sliderValues } : {},
    createdAt: new Date().toISOString(),
  };
  const all = loadCustomFx();
  all.unshift(entry);
  saveAll(all);
  return entry;
}

export function updateCustomFx(id, patch) {
  const all = loadCustomFx();
  const idx = all.findIndex((p) => p.id === id);
  if (idx < 0) return null;
  const next = { ...all[idx], ...patch };
  if (patch.source !== undefined) next.source = String(patch.source).slice(0, 8 * 1024);
  if (patch.sliderValues) next.sliderValues = { ...patch.sliderValues };
  all[idx] = next;
  saveAll(all);
  return next;
}

export function removeCustomFx(id) {
  const all = loadCustomFx();
  const next = all.filter((p) => p.id !== id);
  if (next.length === all.length) return false;
  saveAll(next);
  return true;
}

export function findCustomFx(id) {
  return loadCustomFx().find((p) => p.id === id) || null;
}

export function isCustomFxId(id) {
  return typeof id === "string" && id.startsWith("custom-fx-");
}
